import {
  Eye,
  Calendar,
  FileText,
  Plus,
  Layers,
  Glasses,
  Wrench,
  ClipboardList,
  IndianRupee,
  Truck,
  Stethoscope,
  Sparkles,
} from "lucide-react";

const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

function Cell({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-center transition hover:bg-white hover:shadow-sm">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <h4 className="mt-1 text-base font-bold text-slate-900">{value || "-"}</h4>
    </div>
  );
}

function EyeGrid({ eye, sph, cyl, axis, vn, add, tint }) {
  const cols = add !== undefined ? 5 : (vn !== undefined ? 4 : 3);
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3 space-y-2">
      <div className="flex items-center gap-1.5">
        <span className={`grid h-5 w-5 place-items-center rounded text-[10px] font-bold ${tint}`}>
          {eye}
        </span>
        <span className="text-[11px] font-medium text-slate-600">
          {eye === "R" ? "Right Eye" : "Left Eye"}
        </span>
      </div>
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        <Cell label="SPH" value={sph} />
        <Cell label="CYL" value={cyl} />
        <Cell label="AXIS" value={axis} />
        {vn !== undefined && <Cell label="VN" value={vn} />}
        {add !== undefined && <Cell label="ADD" value={add} />}
      </div>
    </div>
  );
}

function Section({ title, subtitle, icon: Icon, tint, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="mb-4 flex items-center gap-2.5">
        <div className={`h-9 w-9 rounded-lg grid place-items-center ${tint}`}>
          <Icon size={16} />
        </div>
        <div>
          <h4 className="text-sm font-bold text-slate-900 leading-tight">{title}</h4>
          {subtitle && <p className="text-[11px] text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Kv({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-800">{value || "—"}</p>
    </div>
  );
}

// ---- View sections per visit_for -----------------------------------

function BasicPrescriptionView({ p }) {
  return (
    <Section title="Prescription" subtitle="Refraction values" icon={Eye} tint="bg-blue-100 text-blue-700">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <EyeGrid eye="R" tint="bg-blue-100 text-blue-700"
          sph={p.right_sph} cyl={p.right_cyl} axis={p.right_axis} vn={p.right_vn} />
        <EyeGrid eye="L" tint="bg-violet-100 text-violet-700"
          sph={p.left_sph} cyl={p.left_cyl} axis={p.left_axis} vn={p.left_vn} />
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-lg bg-amber-50 border border-amber-100 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">Add Power</p>
          <p className="mt-0.5 text-lg font-bold text-amber-900">{p.add_power || "—"}</p>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Remarks</p>
          <p className="mt-0.5 text-sm text-slate-700 line-clamp-3">{p.remarks || "—"}</p>
        </div>
      </div>
    </Section>
  );
}

function EyeTestView({ p }) {
  const has = (prefix, cols) => cols.some((c) => p[`${prefix}_r_${c}`] || p[`${prefix}_l_${c}`]);
  return (
    <>
      {(p.chief_complaint || p.history) && (
        <Section title="Complaint & History" subtitle="Patient reported" icon={ClipboardList} tint="bg-blue-100 text-blue-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Chief Complaint</p>
              <p className="mt-0.5 text-sm text-slate-800 whitespace-pre-line">{p.chief_complaint || "—"}</p>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">History</p>
              <p className="mt-0.5 text-sm text-slate-800 whitespace-pre-line">{p.history || "—"}</p>
            </div>
          </div>
        </Section>
      )}
      {(p.vision_aided_r || p.vision_aided_l || p.vision_unaided_r || p.vision_unaided_l) && (
        <Section title="Vision" subtitle="Aided & Unaided (Snellen)" icon={Eye} tint="bg-emerald-100 text-emerald-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
              <p className="text-[11px] font-semibold text-slate-600 mb-2">Vision (Aided)</p>
              <div className="grid grid-cols-2 gap-2">
                <Cell label="R" value={p.vision_aided_r} />
                <Cell label="L" value={p.vision_aided_l} />
              </div>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
              <p className="text-[11px] font-semibold text-slate-600 mb-2">Vision (Unaided)</p>
              <div className="grid grid-cols-2 gap-2">
                <Cell label="R" value={p.vision_unaided_r} />
                <Cell label="L" value={p.vision_unaided_l} />
              </div>
            </div>
          </div>
        </Section>
      )}
      {has("pg", ["sph", "cyl", "axis", "add"]) && (
        <Section title="P.G. (Previous Glass)" subtitle="Existing spectacle power" icon={Eye} tint="bg-amber-100 text-amber-700">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <EyeGrid eye="R" tint="bg-blue-100 text-blue-700"
              sph={p.pg_r_sph} cyl={p.pg_r_cyl} axis={p.pg_r_axis} add={p.pg_r_add} />
            <EyeGrid eye="L" tint="bg-violet-100 text-violet-700"
              sph={p.pg_l_sph} cyl={p.pg_l_cyl} axis={p.pg_l_axis} add={p.pg_l_add} />
          </div>
          {p.pg_remarks && (
            <p className="mt-3 text-xs text-slate-600 rounded-lg bg-slate-50 border border-slate-100 p-2.5">
              <span className="font-semibold text-slate-700">Remarks:</span> {p.pg_remarks}
            </p>
          )}
        </Section>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {has("ar1", ["sph", "cyl", "axis"]) && (
          <Section title="AR 1" subtitle="Auto Refractor — reading 1" icon={Stethoscope} tint="bg-sky-100 text-sky-700">
            <div className="space-y-3">
              <EyeGrid eye="R" tint="bg-blue-100 text-blue-700" sph={p.ar1_r_sph} cyl={p.ar1_r_cyl} axis={p.ar1_r_axis} />
              <EyeGrid eye="L" tint="bg-violet-100 text-violet-700" sph={p.ar1_l_sph} cyl={p.ar1_l_cyl} axis={p.ar1_l_axis} />
            </div>
          </Section>
        )}
        {has("ar2", ["sph", "cyl", "axis"]) && (
          <Section title="AR 2" subtitle="Auto Refractor — reading 2" icon={Stethoscope} tint="bg-sky-100 text-sky-700">
            <div className="space-y-3">
              <EyeGrid eye="R" tint="bg-blue-100 text-blue-700" sph={p.ar2_r_sph} cyl={p.ar2_r_cyl} axis={p.ar2_r_axis} />
              <EyeGrid eye="L" tint="bg-violet-100 text-violet-700" sph={p.ar2_l_sph} cyl={p.ar2_l_cyl} axis={p.ar2_l_axis} />
            </div>
          </Section>
        )}
      </div>
      {has("subj", ["sph", "cyl", "axis", "vn", "add"]) && (
        <Section title="Subjective Refraction" subtitle="Patient-verified refinement" icon={Sparkles} tint="bg-emerald-100 text-emerald-700">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <EyeGrid eye="R" tint="bg-blue-100 text-blue-700"
              sph={p.subj_r_sph} cyl={p.subj_r_cyl} axis={p.subj_r_axis} vn={p.subj_r_vn} add={p.subj_r_add} />
            <EyeGrid eye="L" tint="bg-violet-100 text-violet-700"
              sph={p.subj_l_sph} cyl={p.subj_l_cyl} axis={p.subj_l_axis} vn={p.subj_l_vn} add={p.subj_l_add} />
          </div>
        </Section>
      )}
      {p.subj_remarks && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Subjective Refraction Remarks</p>
          <p className="text-sm text-slate-800 whitespace-pre-line">{p.subj_remarks}</p>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {has("dry", ["sph", "cyl", "axis"]) && (
          <Section title="Dilated Refraction" subtitle="Dilated refraction (retinoscopy)" icon={Eye} tint="bg-rose-100 text-rose-700">
            <div className="space-y-3">
              <EyeGrid eye="R" tint="bg-blue-100 text-blue-700"
                sph={p.dry_r_sph} cyl={p.dry_r_cyl} axis={p.dry_r_axis} vn={p.dry_r_vn} add={p.dry_r_add} />
              <EyeGrid eye="L" tint="bg-violet-100 text-violet-700"
                sph={p.dry_l_sph} cyl={p.dry_l_cyl} axis={p.dry_l_axis} vn={p.dry_l_vn} add={p.dry_l_add} />
            </div>
          </Section>
        )}
        {has("pmt", ["sph", "cyl", "axis"]) && (
          <Section title="PMT" subtitle="Post-mydriatic test" icon={Eye} tint="bg-violet-100 text-violet-700">
            <div className="space-y-3">
              <EyeGrid eye="R" tint="bg-blue-100 text-blue-700"
                sph={p.pmt_r_sph} cyl={p.pmt_r_cyl} axis={p.pmt_r_axis} vn={p.pmt_r_vn} add={p.pmt_r_add} />
              <EyeGrid eye="L" tint="bg-violet-100 text-violet-700"
                sph={p.pmt_l_sph} cyl={p.pmt_l_cyl} axis={p.pmt_l_axis} vn={p.pmt_l_vn} add={p.pmt_l_add} />
            </div>
          </Section>
        )}
      </div>
      {p.eye_test_remarks && (
        <Section title="Final Remarks" subtitle="Observation & recommendation" icon={FileText} tint="bg-slate-100 text-slate-700">
          <p className="text-sm text-slate-700 whitespace-pre-line">{p.eye_test_remarks}</p>
        </Section>
      )}
    </>
  );
}

function LensOrderView({ p }) {
  return (
    <Section title="Lens Order" subtitle="Lens specs, pricing & delivery" icon={Layers} tint="bg-emerald-100 text-emerald-700">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <Kv label="Lens Type" value={p.lens_type} />
        <Kv label="Material" value={p.lens_material} />
        <Kv label="Coating" value={p.lens_coating} />
        <Kv label="Tint" value={p.lens_tint} />
        <Kv label="Brand" value={p.lens_brand} />
        <Kv label="Cost (₹)" value={p.lens_cost} />
        <Kv label="Delivery" value={formatDate(p.lens_delivery_date)} />
        <Kv label="Frame Condition" value={p.lens_frame_condition} />
      </div>
      {p.lens_notes && (
        <p className="mt-3 text-xs text-slate-600 rounded-lg bg-slate-50 border border-slate-100 p-2.5 whitespace-pre-line">
          <span className="font-semibold text-slate-700">Notes:</span> {p.lens_notes}
        </p>
      )}
    </Section>
  );
}

function SpectaclesOrderView({ p }) {
  return (
    <>
      <Section title="Frame" subtitle="Frame details" icon={Glasses} tint="bg-violet-100 text-violet-700">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <Kv label="Model / SKU" value={p.spec_frame_model} />
          <Kv label="Brand" value={p.spec_frame_brand} />
          <Kv label="Color" value={p.spec_frame_color} />
          <Kv label="Material" value={p.spec_frame_material} />
          <Kv label="Shape" value={p.spec_frame_shape} />
          <Kv label="Size" value={p.spec_frame_size} />
        </div>
      </Section>
      <Section title="Lens" subtitle="Lens details" icon={Layers} tint="bg-emerald-100 text-emerald-700">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <Kv label="Type" value={p.spec_lens_type} />
          <Kv label="Material" value={p.spec_lens_material} />
          <Kv label="Coating" value={p.spec_lens_coating} />
          <Kv label="Tint" value={p.spec_lens_tint} />
          <Kv label="Brand" value={p.spec_lens_brand} />
        </div>
      </Section>
      <Section title="Pricing & Delivery" subtitle="Payment breakdown" icon={IndianRupee} tint="bg-amber-100 text-amber-700">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Kv label="Frame ₹" value={p.spec_frame_cost} />
          <Kv label="Lens ₹" value={p.spec_lens_cost} />
          <Kv label="Total ₹" value={p.spec_total} />
          <Kv label="Advance ₹" value={p.spec_advance} />
          <Kv label="Balance ₹" value={p.spec_balance} />
          <Kv label="Delivery" value={formatDate(p.spec_delivery_date)} />
        </div>
        {p.spec_notes && (
          <p className="mt-3 text-xs text-slate-600 rounded-lg bg-slate-50 border border-slate-100 p-2.5 whitespace-pre-line">
            <span className="font-semibold text-slate-700">Notes:</span> {p.spec_notes}
          </p>
        )}
      </Section>
    </>
  );
}

function RepairView({ p }) {
  return (
    <Section title="Repair Ticket" subtitle="Problem, parts & status" icon={Wrench} tint="bg-amber-100 text-amber-700">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <Kv label="Type" value={p.repair_type} />
        <Kv label="Status" value={p.repair_status} />
        <Kv label="Parts" value={p.repair_parts} />
        <Kv label="Cost (₹)" value={p.repair_cost} />
        <Kv label="Delivery" value={formatDate(p.repair_delivery_date)} />
      </div>
      {p.repair_problem && (
        <div className="mt-3 rounded-lg bg-rose-50 border border-rose-100 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-rose-700">Problem</p>
          <p className="mt-0.5 text-sm text-slate-800 whitespace-pre-line">{p.repair_problem}</p>
        </div>
      )}
      {p.repair_notes && (
        <p className="mt-3 text-xs text-slate-600 rounded-lg bg-slate-50 border border-slate-100 p-2.5 whitespace-pre-line">
          <span className="font-semibold text-slate-700">Notes:</span> {p.repair_notes}
        </p>
      )}
    </Section>
  );
}

// ---- Main card ------------------------------------------------------

export default function PrescriptionCard({ prescription, index, visitFor }) {
  const p = prescription || {};

  const meta =
    visitFor === "Eye Test"
      ? { title: "Eye Test", tint: "from-blue-600 via-sky-600 to-cyan-600", icon: Eye }
      : visitFor === "Lens Only"
      ? { title: "Lens Order", tint: "from-emerald-600 via-teal-600 to-cyan-600", icon: Layers }
      : visitFor === "Complete Specs"
      ? { title: "Spectacles Order", tint: "from-violet-600 via-fuchsia-600 to-pink-600", icon: Glasses }
      : visitFor === "Frame Only"
      ? { title: "Frame Order", tint: "from-violet-600 via-fuchsia-600 to-pink-600", icon: Glasses }
      : visitFor === "Specs Repair"
      ? { title: "Repair Ticket", tint: "from-amber-500 via-orange-500 to-rose-500", icon: Wrench }
      : { title: "Prescription", tint: "from-blue-600 via-indigo-600 to-violet-700", icon: Eye };

  const HeaderIcon = meta.icon;

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      {/* Header */}
      <div className={`bg-gradient-to-r ${meta.tint} px-6 py-5`}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-white/20 p-2.5 backdrop-blur">
              <HeaderIcon className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-white">
                {meta.title} #{index + 1}
              </h2>
              <p className="text-xs text-white/85 mt-0.5">
                Record {p.prescription_no || index + 1}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="rounded-lg bg-white/15 px-3 py-1.5 backdrop-blur flex items-center gap-1.5">
              <Calendar size={14} className="text-white" />
              <span className="text-xs font-medium text-white">
                {formatDate(p.prescription_date)}
              </span>
            </div>
            <div className="rounded-lg bg-white/15 px-3 py-1.5 backdrop-blur flex items-center gap-1.5">
              <FileText size={14} className="text-white" />
              <span className="text-xs font-medium text-white">#{p.prescription_no}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 space-y-4">
        {visitFor === "Eye Test" ? (
          <EyeTestView p={p} />
        ) : visitFor === "Lens Only" ? (
          <>
            <BasicPrescriptionView p={p} />
            <LensOrderView p={p} />
          </>
        ) : visitFor === "Complete Specs" ? (
          <>
            <BasicPrescriptionView p={p} />
            <SpectaclesOrderView p={p} />
          </>
        ) : visitFor === "Specs Repair" ? (
          <RepairView p={p} />
        ) : (
          <BasicPrescriptionView p={p} />
        )}
      </div>
    </div>
  );
}
